import React from 'react';
import { createPortal } from 'react-dom';
import { useSortableDragActive } from './sortable';
import { usePanelLabel } from '../primitives/labels';
import { SortableContext } from './sortable';
import {
    TooltipProvider,
    Tooltip,
    TooltipTrigger,
    TooltipContent,
} from '../primitives/Tooltip';
import ToolMenu from './ToolMenu';
import useToolbar from './useToolbar';
import { ToolGroupProps, GenericToolbarProps } from './types';

import '../theme/toolbar.css';

/** Fallback tool each group shows before the user ever picks a sub-tool.
 Photoshop-style: the paint bucket (not the gradient) leads its group. */
const DEFAULT_GROUP_TOOL: Record<string, string> = {
    gradient: 'paint-bucket',
};

/** Strips a hardcoded trailing key hint ("Move Tool (V)" -> "Move Tool") so
 the tooltip can show the registry-driven shortcut exactly once. */
function stripHardcodedHint(name: string): string {
    return name.replace(/\s*\([^()]*\)\s*$/, '');
}

function resolveGroupFace(
    group: ToolGroupProps,
    selectedTool: { groupId: string; toolId: string },
    groupActiveTools: Record<string, string>,
) {
    // The group the user last interacted with shows exactly what they picked;
    // every other group shows its remembered face or its default
    if (group.groupId === selectedTool.groupId) {
        const picked = group.tools.find(
            (tool) => tool.id === selectedTool.toolId,
        );
        if (picked) return picked;
    }
    const remembered = groupActiveTools[group.groupId];
    if (remembered) {
        const saved = group.tools.find((tool) => tool.id === remembered);
        if (saved) return saved;
    }
    const defaultId = DEFAULT_GROUP_TOOL[group.groupId];
    if (defaultId) {
        const fallback = group.tools.find((tool) => tool.id === defaultId);
        if (fallback) return fallback;
    }
    return group.tools[0];
}

function GenericToolbar({
    toolGroups,
    initialSelectedTool,
    longPressDelay = 300,
    direction = 'vertical',
    className = '',
    onToolSelect,
    renderGroup,
    sortable = false,
    locked = false,
    shortcutLabels = {},
    groupActiveTools = {},
    onSortEnd,
}: GenericToolbarProps) {
    const t = usePanelLabel();

    // While a toolbar drag-reorder is in progress, click and long-press
    // semantics are suspended so submenus never open mid-drag and a drag
    // release never selects a tool.
    const activeDrag = useSortableDragActive();
    const isDragActive = activeDrag;
    const {
        selectedTool,
        activeMenu,
        menuPosition,
        activeIconRef,
        portalTarget,
        handleMouseDown,
        handleMouseUp,
        selectTool,
    } = useToolbar(
        toolGroups,
        initialSelectedTool,
        longPressDelay,
        direction,
        onToolSelect,
        isDragActive,
    );

    // The rail's axis drives the sortable slide direction and the tooltip
    // side: a vertical rail opens submenus sideways, a horizontal rail below
    const isHorizontal = direction === 'horizontal';
    const tooltipSide = isHorizontal ? 'bottom' : 'right';

    const body = (
        <div className={className}>
            {toolGroups.map((group) => {
                    const activeTool = resolveGroupFace(
                        group,
                        selectedTool,
                        groupActiveTools,
                    );
                    const hasMultipleTools = group.tools.length > 1;
                    const node = (
                        <div key={group.groupId}>
                            {activeMenu !== group.groupId ? (
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <div
                                            className={`icon-container text-[var(--foreground)] flex items-center justify-center cursor-pointer relative focus-ring ${
                                                selectedTool.groupId ===
                                                group.groupId
                                                    ? 'is-selected'
                                                    : ''
                                            } ${hasMultipleTools ? 'has-submenu' : ''}`}
                                            role="button"
                                            tabIndex={0}
                                            aria-pressed={
                                                selectedTool.groupId ===
                                                group.groupId
                                            }
                                            aria-label={t(
                                                `tools.${activeTool.id}`,
                                                stripHardcodedHint(activeTool.name),
                                            )}
                                            onMouseDown={(e) =>
                                                handleMouseDown(
                                                    e,
                                                    group.groupId,
                                                )
                                            }
                                            onMouseUp={(e) =>
                                                handleMouseUp(
                                                    e,
                                                    group.groupId,
                                                    activeTool.id,
                                                )
                                            }
                                            onKeyDown={(e) => {
                                                if (
                                                    e.key !== 'Enter' &&
                                                    e.key !== ' '
                                                )
                                                    return;
                                                e.preventDefault();
                                                if (!isDragActive)
                                                    selectTool(
                                                        group.groupId,
                                                        activeTool.id,
                                                    );
                                            }}
                                            onContextMenu={(e) =>
                                                e.preventDefault()
                                            }
                                        >
                                            <activeTool.icon className="w-[var(--toolbar-icon-size)] h-[var(--toolbar-icon-size)]" />
                                        </div>
                                    </TooltipTrigger>
                                    <TooltipContent
                                        side={tooltipSide}
                                        className="tooltip-content"
                                    >
                                        {t(`tools.${activeTool.id}`,
                                            stripHardcodedHint(activeTool.name),
                                        )}
                                        {shortcutLabels[activeTool.id]
                                            ? ` (${shortcutLabels[activeTool.id]})`
                                            : ''}
                                    </TooltipContent>
                                </Tooltip>
                            ) : (
                                <div
                                    className={`icon-container text-[var(--foreground)] flex items-center justify-center cursor-pointer relative focus-ring ${
                                        selectedTool.groupId === group.groupId
                                            ? 'is-selected'
                                            : ''
                                    } ${hasMultipleTools ? 'has-submenu' : ''}`}
                                    role="button"
                                    tabIndex={0}
                                    aria-pressed={
                                        selectedTool.groupId === group.groupId
                                    }
                                    aria-label={t(
                                        `tools.${activeTool.id}`,
                                        stripHardcodedHint(activeTool.name),
                                    )}
                                    onMouseDown={(e) =>
                                        handleMouseDown(e, group.groupId)
                                    }
                                    onMouseUp={(e) =>
                                        handleMouseUp(
                                            e,
                                            group.groupId,
                                            activeTool.id,
                                        )
                                    }
                                    onKeyDown={(e) => {
                                        if (e.key !== 'Enter' && e.key !== ' ')
                                            return;
                                        e.preventDefault();
                                        if (!isDragActive)
                                            selectTool(
                                                group.groupId,
                                                activeTool.id,
                                            );
                                    }}
                                    onContextMenu={(e) => e.preventDefault()}
                                >
                                    <activeTool.icon className="w-[var(--toolbar-icon-size)] h-[var(--toolbar-icon-size)]" />
                                </div>
                            )}
                            {portalTarget &&
                                activeMenu === group.groupId &&
                                hasMultipleTools &&
                                menuPosition &&
                                !isDragActive &&
                                createPortal(
                                    <ToolMenu
                                        tools={group.tools}
                                        shortcutLabels={shortcutLabels}
                                        onSelect={(toolId) =>
                                            selectTool(group.groupId, toolId)
                                        }
                                        style={{
                                            ...menuPosition,
                                            position: 'fixed',
                                            zIndex: 110,
                                        }}
                                    />,
                                    portalTarget,
                                )}
                        </div>
                    );
                    return renderGroup ? (
                        <React.Fragment key={group.groupId}>
                            {renderGroup(group, node, locked)}
                        </React.Fragment>
                    ) : (
                        node
                    );
                })}
            </div>
        );

    return (
        <TooltipProvider>
            {sortable ? (
                <SortableContext
                    items={toolGroups.map((group) => group.groupId)}
                    direction={isHorizontal ? 'horizontal' : 'vertical'}
                    onSortEnd={onSortEnd}
                >
                    {body}
                </SortableContext>
            ) : (
                body
            )}
        </TooltipProvider>
    );
}

export { GenericToolbar };
